import {
  Badge,
  Button,
  Checkbox,
  createTableColumn,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Skeleton,
  SkeletonItem,
  type TableColumnDefinition,
  Text,
  Textarea,
  Toolbar,
  ToolbarButton,
  tokens,
} from "@fluentui/react-components"
import {
  AddRegular,
  ArrowClockwiseRegular,
  DeleteRegular,
  EditRegular,
  KeyMultipleRegular,
  MoreHorizontalRegular,
  PersonTagRegular,
  SearchRegular,
} from "@fluentui/react-icons"
import type { Page, Role, Scope } from "@rema/shared"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { ConfirmDialog } from "../../components/confirm-dialog"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { FormField } from "../../components/form-field"
import { PageHeader } from "../../components/page-header"
import { es } from "../../i18n/es"
import { api, isApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { rolesI18n as t } from "./i18n"

const ROLES_QUERY_KEY = ["admin", "roles"] as const
const SCOPES_CATALOG_KEY = ["admin", "scopes", "catalog"] as const

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: {
    minWidth: "240px",
  },
  matrix: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    maxHeight: "55vh",
    overflowY: "auto",
    paddingRight: tokens.spacingHorizontalS,
  },
  matrixHeader: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalS,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  scopeKey: {
    fontFamily: tokens.fontFamilyMonospace,
  },
  skeleton: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
})

interface RoleFormValues {
  name: string
  description: string
}

// Catálogo de roles + matriz roles×scopes (M3 · F3.2/F6.7).
export const RolesPage = () => {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const [scopesFor, setScopesFor] = useState<Role | null>(null)
  const [toDelete, setToDelete] = useState<Role | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const { control, handleSubmit, reset, setError } = useForm<RoleFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { name: "", description: "" },
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY })

  const saveMutation = useMutation({
    mutationFn: async (values: RoleFormValues) => {
      const description = values.description.trim() || null
      if (editing) {
        return api.patch<Role>(`/admin/roles/${editing.id}`, {
          name: values.name.trim(),
          description,
        })
      }
      return api.post<Role>("/admin/roles", {
        name: values.name.trim(),
        description: description ?? undefined,
      })
    },
    onSuccess: () => {
      invalidate()
      closeDrawer()
    },
    onError: (error) => {
      if (isApiError(error)) {
        const mapped = applyFieldErrors(error.problem, setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        setFormError(editing ? t.updateError : t.createError)
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (role: Role) => api.delete<void>(`/admin/roles/${role.id}`),
    onSuccess: () => {
      invalidate()
      setToDelete(null)
    },
  })

  const openCreate = () => {
    setEditing(null)
    setFormError(null)
    reset({ name: "", description: "" })
    setDrawerOpen(true)
  }

  const openEdit = (role: Role) => {
    setEditing(role)
    setFormError(null)
    reset({ name: role.name, description: role.description ?? "" })
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setEditing(null)
    setFormError(null)
  }

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    saveMutation.mutate(values)
  })

  const fetchPage = async (cursor: string | null): Promise<Page<Role>> =>
    api.get<Page<Role>>("/admin/roles", {
      query: { limit: 50, cursor: cursor ?? undefined, q: search.trim() || undefined },
    })

  const columns = buildColumns({
    onEdit: openEdit,
    onEditScopes: setScopesFor,
    onDelete: setToDelete,
  })

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={t.searchLabel}>
      <Input
        className={styles.search}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
        placeholder={t.searchPlaceholder}
        contentBefore={<SearchRegular />}
        aria-label={t.searchLabel}
      />
    </Toolbar>
  )

  return (
    <>
      <PageHeader
        title={es.nav.roles}
        description={t.pageDescription}
        actions={
          <Toolbar aria-label={t.newRole}>
            <ToolbarButton appearance="primary" icon={<AddRegular />} onClick={openCreate}>
              {t.newRole}
            </ToolbarButton>
            <ToolbarButton
              icon={<ArrowClockwiseRegular />}
              aria-label={t.refresh}
              onClick={() => invalidate()}
            />
          </Toolbar>
        }
      />
      <DataGridPage<Role>
        queryKey={[...ROLES_QUERY_KEY, search.trim()]}
        fetchPage={fetchPage}
        columns={columns}
        getRowId={(role) => role.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<PersonTagRegular />}
            title={t.emptyTitle}
            description={t.emptyDescription}
            action={
              <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                {t.emptyAction}
              </Button>
            }
          />
        }
      />

      <AppDrawerForm
        open={drawerOpen}
        title={editing ? t.editTitle : t.createTitle}
        onSubmit={onSubmit}
        onClose={closeDrawer}
        submitting={saveMutation.isPending}
        error={formError}
      >
        <FormField
          control={control}
          name="name"
          label={t.nameLabel}
          required
          hint={t.nameHint}
          rules={{ required: t.nameRequired }}
        >
          {(field, invalid) => (
            <Input
              {...field}
              type="text"
              autoFocus
              placeholder={t.namePlaceholder}
              aria-invalid={invalid}
            />
          )}
        </FormField>
        <FormField control={control} name="description" label={t.descriptionLabel}>
          {(field) => (
            <Textarea {...field} resize="vertical" placeholder={t.descriptionPlaceholder} />
          )}
        </FormField>
      </AppDrawerForm>

      <ScopeMatrixDrawer
        role={scopesFor}
        onClose={() => setScopesFor(null)}
        onSaved={() => {
          setScopesFor(null)
          invalidate()
        }}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title={t.deleteTitle}
        message={toDelete ? t.deleteMessage(toDelete.name) : ""}
        confirmLabel={t.deleteConfirm}
        danger
        confirming={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete)}
        onCancel={() => setToDelete(null)}
      />
    </>
  )
}

// --- Editor de la matriz de scopes del rol (PUT /admin/roles/:id/scopes) ---------

interface ScopeMatrixDrawerProps {
  role: Role | null
  onClose: () => void
  onSaved: () => void
}

const ScopeMatrixDrawer = ({ role, onClose, onSaved }: ScopeMatrixDrawerProps) => {
  const styles = useStyles()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  // Catálogo completo de scopes visibles (alcance del actor lo fija el servidor).
  const scopesQuery = useQuery({
    queryKey: SCOPES_CATALOG_KEY,
    queryFn: () => api.get<Page<Scope>>("/admin/scopes", { query: { limit: 100 } }),
    enabled: role !== null,
  })

  const catalog = useMemo(() => scopesQuery.data?.data ?? [], [scopesQuery.data])

  // Al abrir para un rol, precarga sus scopes actuales (por key → id del catálogo).
  useEffect(() => {
    if (!role) return
    const keyToId = new Map(catalog.map((s) => [s.key, s.id]))
    const initial = new Set<string>()
    for (const key of role.scopes) {
      const id = keyToId.get(key)
      if (id) initial.add(id)
    }
    setSelected(initial)
    setError(null)
  }, [role, catalog])

  const saveMutation = useMutation({
    mutationFn: (scopeIds: string[]) =>
      api.put<Role>(`/admin/roles/${role?.id}/scopes`, { scope_ids: scopeIds }),
    onSuccess: onSaved,
    onError: (err) => {
      setError(isApiError(err) ? problemMessage(err.problem) : t.scopesError)
    },
  })

  const toggle = (scopeId: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(scopeId)
      else next.delete(scopeId)
      return next
    })
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    saveMutation.mutate([...selected])
  }

  let body: ReactNode
  if (scopesQuery.isPending && role) {
    body = (
      <Skeleton className={styles.skeleton} aria-label={es.common.loading}>
        {Array.from({ length: 6 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: filas de skeleton estáticas
          <SkeletonItem key={i} size={24} />
        ))}
      </Skeleton>
    )
  } else if (scopesQuery.isError) {
    body = (
      <MessageBar intent="error">
        <MessageBarBody>{t.loadScopesError}</MessageBarBody>
      </MessageBar>
    )
  } else if (catalog.length === 0) {
    body = <Text className={styles.subtitle}>{t.scopesEmpty}</Text>
  } else {
    body = (
      <div className={styles.matrix}>
        {catalog.map((scope) => (
          <Checkbox
            key={scope.id}
            checked={selected.has(scope.id)}
            onChange={(_, data) => toggle(scope.id, data.checked === true)}
            label={{ children: <span className={styles.scopeKey}>{scope.key}</span> }}
          />
        ))}
      </div>
    )
  }

  return (
    <AppDrawerForm
      open={role !== null}
      title={role ? `${t.scopesTitle} · ${role.name}` : t.scopesTitle}
      onSubmit={onSubmit}
      onClose={onClose}
      submitLabel={t.saveScopes}
      submitting={saveMutation.isPending}
      error={error}
    >
      <div className={styles.matrixHeader}>
        <Text className={styles.subtitle}>{t.scopesSubtitle}</Text>
        <Text weight="semibold">{t.selectedCount(selected.size)}</Text>
      </div>
      {body}
    </AppDrawerForm>
  )
}

// --- Columnas del DataGrid -------------------------------------------------------

interface ColumnHandlers {
  onEdit: (role: Role) => void
  onEditScopes: (role: Role) => void
  onDelete: (role: Role) => void
}

function buildColumns({
  onEdit,
  onEditScopes,
  onDelete,
}: ColumnHandlers): TableColumnDefinition<Role>[] {
  return [
    createTableColumn<Role>({
      columnId: "name",
      renderHeaderCell: () => t.colName,
      renderCell: (role) => (
        <span
          style={{ display: "inline-flex", gap: tokens.spacingHorizontalXS, alignItems: "center" }}
        >
          <Text weight="semibold">{role.name}</Text>
          {role.is_system ? (
            <Badge appearance="tint" color="informative">
              {t.systemBadge}
            </Badge>
          ) : null}
        </span>
      ),
    }),
    createTableColumn<Role>({
      columnId: "description",
      renderHeaderCell: () => t.colDescription,
      renderCell: (role) => role.description ?? "—",
    }),
    createTableColumn<Role>({
      columnId: "scopes",
      renderHeaderCell: () => t.colScopes,
      renderCell: (role) => (
        <Badge appearance="outline" color={role.scopes.length > 0 ? "brand" : "subtle"}>
          {role.scopes.length}
        </Badge>
      ),
    }),
    createTableColumn<Role>({
      columnId: "scope",
      renderHeaderCell: () => t.colScope,
      renderCell: (role) => (
        <Badge appearance="outline" color={role.domain_id ? "brand" : "informative"}>
          {role.domain_id ? t.scopeDomain : t.scopeGlobal}
        </Badge>
      ),
    }),
    createTableColumn<Role>({
      columnId: "actions",
      renderHeaderCell: () => t.colActions,
      renderCell: (role) => (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              aria-label={t.rowActions}
              icon={<MoreHorizontalRegular />}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<KeyMultipleRegular />} onClick={() => onEditScopes(role)}>
                {t.editScopes}
              </MenuItem>
              <MenuItem icon={<EditRegular />} onClick={() => onEdit(role)}>
                {t.editRole}
              </MenuItem>
              <MenuItem
                icon={<DeleteRegular />}
                disabled={role.is_system}
                onClick={() => onDelete(role)}
              >
                {t.deleteRole}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ),
    }),
  ]
}
